
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  Ref,
  ref,
} from 'vue';
import { UserState } from '@/composables/User';
import { DEFAULT_CURRENCY } from '@/components/template-builder/utils/constants';
import { formatNumbersInString, formatNumberToCurrency } from '@/helpers/Number';
import Knob from 'primevue/knob';
import type { SpmKpiDataPoint } from '@/types/stats-types';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import {
  getStatsLabelKeyFromSlug,
  normalizeStatsLabelSlug,
} from '@/helpers/statsLabels';
import type { StatsLabelContext } from '@/helpers/statsLabels';
import StatsEmptyState from '@/components/stats/StatsEmptyState.vue';

export default defineComponent({
  name: 'SpmKpi',

  components: {
    Knob,
    StatsEmptyState,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    kpiName: {
      type: String,
      required: true,
    },

    kpiData: {
      type: Array as PropType<SpmKpiDataPoint[]>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const data: Ref<SpmKpiDataPoint[]> = ref(props.kpiData);
    const kpiGridRef = ref<HTMLElement | null>(null);
    const isScrolledToBottom = ref(false);
    const hasScroll = ref(false);

    const statsIcons: Record<StatsLabelContext | string, Record<string, string>> = {
      channels: {
        email: 'fa-regular fa-envelope',
        display: 'fa-regular fa-desktop',
        popup: 'fa-regular fa-share',
        embed: 'fa-regular fa-list-alt',
        pushnotifications: 'fa-regular fa-bell',
        sms: 'fa-regular fa-sms',
        whatsapp: 'fa-brands fa-whatsapp',
        richtexting: 'fa-regular fa-message-smile',
      },

      boxes: {
        email: 'fa-regular fa-envelope',
        embed: 'fa-regular fa-list-alt',
        popup: 'fa-regular fa-share',
        pushnotifications: 'fa-regular fa-bell',
        sms: 'fa-regular fa-sms',
        rcs: 'fa-regular fa-message-smile',
        whatsapp: 'fa-brands fa-whatsapp',
      },

      registrations: {
        registration: 'fa-regular fa-store',
        newsletter: 'fa-regular fa-envelope',
      },

      bouncesTypesIntegers: {
        'hard-bounce': 'fa-regular fa-ban',
        'soft-bounce': 'fa-regular fa-triangle-exclamation',
        spam: 'fa-regular fa-shield-exclamation',
        unsubscription: 'fa-regular fa-user-slash',
      },
    };

    const hasData = data.value.length > 0;

    const translateStatLabel = (
      value: string | null | undefined,
      context?: StatsLabelContext,
    ): string => {
      if (!value) {
        return '';
      }

      const translationKey = getStatsLabelKeyFromSlug(value, context);
      return translationKey.includes('.') ? t(translationKey) : translationKey;
    };

    data.value.forEach((kpi) => {
      const current = kpi;

      const context = (typeof current.icon === 'string'
        ? current.icon
        : undefined) as StatsLabelContext | undefined;

      const slug = typeof current.label === 'string'
        ? normalizeStatsLabelSlug(current.label)
        : '';

      const iconGroup = context ? statsIcons[context] : undefined;
      const icon = iconGroup ? iconGroup[slug] ?? null : null;

      if (current.label && !current.skipLabelTranslation) {
        current.label = translateStatLabel(current.label, context);
      }

      if (current.legend && !current.skipLabelTranslation) {
        current.legend = translateStatLabel(current.legend, context);
      }

      if (icon) {
        current.icon = icon;
      }

      // Les valeurs sont déjà formatées par l'API, pas besoin de les reformater
      // Le formatage était : formatNumberToCurrency() pour type 'price' et formatNumbersInString() pour type 'number'
      // Maintenant on garde la valeur telle quelle
    });

    const iconColors = [
      { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }, // Blue
      { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }, // Green
      { bg: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }, // Orange
      { bg: 'rgba(6, 182, 212, 0.1)', color: '#06b6d4' }, // Cyan
      { bg: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }, // Pink
      { bg: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }, // Purple
      { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }, // Amber
      { bg: 'rgba(20, 184, 166, 0.1)', color: '#14b8a6' }, // Teal
    ];

    const getIconBadgeStyle = (kpi: SpmKpiDataPoint, index: number) => {
      if (kpi.isTotal) {
        // Pas de style inline pour les cards Total, le CSS s'en occupe
        return {};
      }

      const colorSet = iconColors[index % iconColors.length];
      return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
      };
    };

    const getGridClass = computed(() => {
      const kpiCount = data.value.length;

      if (kpiCount === 1) {
        return 'kpi-grid--single';
      }
      if (kpiCount === 2) {
        return 'kpi-grid--double';
      }
      if (kpiCount === 3) {
        return 'kpi-grid--triple';
      }
      if (kpiCount === 4) {
        return 'kpi-grid--quad';
      }

      return 'kpi-grid--list';
    });

    const handleScroll = () => {
      if (!kpiGridRef.value) return;

      const { scrollTop, scrollHeight, clientHeight } = kpiGridRef.value;

      // Vérifier si le scroll est nécessaire (contenu dépasse la hauteur visible)
      hasScroll.value = scrollHeight > clientHeight;

      // Considère qu'on est en bas si on est à moins de 10px du bas
      isScrolledToBottom.value = scrollTop + clientHeight >= scrollHeight - 10;
    };

    onMounted(() => {
      if (kpiGridRef.value) {
        kpiGridRef.value.addEventListener('scroll', handleScroll);
        // Vérifier l'état initial après un court délai pour que le rendu soit complet
        setTimeout(handleScroll, 100);
      }

      // Recalculer si le scroll est nécessaire quand la fenêtre change de taille
      window.addEventListener('resize', handleScroll);
    });

    onUnmounted(() => {
      if (kpiGridRef.value) {
        kpiGridRef.value.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleScroll);
    });

    return {
      t,
      hasData,
      data,
      getIconBadgeStyle,
      getGridClass,
      kpiGridRef,
      isScrolledToBottom,
      hasScroll,
    };
  },
});
